import type { ReactElement } from 'react';
import { useState, useCallback, useEffect } from 'react';
import { NeonModalDrawer, NeonButtonFooter } from '@ps-refarch-ux/neon';
import type { GradeDeficiency } from '@/repositories/configurations-repository';
import { NeonButtonTypes } from '@/constants/neon-constants';
import {
  CONFIGURATIONS_ID,
  CREDIT_DEFICIENCY_PANEL_TITLE,
  CREDIT_DEFICIENCY_DESCRIPTION,
  CREDIT_DEFICIENCY_NOTE,
  CREDIT_DEFICIENCY_NOTE_BOLD,
  CREDIT_DEFICIENCY_NOTE_SUFFIX,
  CREDIT_DEFICIENCY_PANEL_COL_GRADE,
  CREDIT_DEFICIENCY_PANEL_COL_MIN_CUMULATIVE,
  CREDIT_DEFICIENCY_PANEL_CANCEL,
  CREDIT_DEFICIENCY_PANEL_SAVE,
  CREDIT_DEFICIENCY_GRADES,
  CREDIT_DEFICIENCY_NO_MINIMUM,
} from '@/constants/configurations-constants';
import {
  bodyTextStyle,
  panelTableWrapperStyle,
  panelTableStyle,
  panelTableHeaderStyle,
  panelTableCellStyle,
  panelTableGradeColStyle,
  panelTableDeficiencyMinColStyle,
  panelInputStyle,
} from './configurations.styles';
import { sanitizeCreditsFromSource } from '@/utils/sanitize-credits-input';

function gradeDeficiencyToRows(items: GradeDeficiency[] | undefined): Record<number, string> {
  const byGrade = new Map(items?.map((s) => [s.grade, s]) ?? []);
  return CREDIT_DEFICIENCY_GRADES.reduce(
    (acc, grade) => {
      const s = byGrade.get(grade);
      acc[grade] =
        s?.minCredits !== undefined && s?.minCredits !== null
          ? sanitizeCreditsFromSource(s.minCredits)
          : '';
      return acc;
    },
    {} as Record<number, string>
  );
}

function rowsToGradeDeficiency(rows: Record<number, string>): GradeDeficiency[] {
  return CREDIT_DEFICIENCY_GRADES.filter((grade) => {
    const value = (rows[grade] ?? '').trim();
    if (value === '') {
      return false;
    }
    const num = Number(value);
    return Number.isFinite(num);
  }).map((grade) => {
    const num = Number((rows[grade] ?? '').trim());
    return { grade, minCredits: num };
  });
}

export type CreditDeficiencyPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  initialGradeDeficiency?: GradeDeficiency[];
  onSave?: (gradeDeficiency: GradeDeficiency[]) => void;
};

export function CreditDeficiencyPanel({
  isOpen,
  onClose,
  initialGradeDeficiency,
  onSave,
}: CreditDeficiencyPanelProps): ReactElement | null {
  const [rows, setRows] = useState<Record<number, string>>(() =>
    gradeDeficiencyToRows(initialGradeDeficiency)
  );

  useEffect(() => {
    if (isOpen) {
      setRows(gradeDeficiencyToRows(initialGradeDeficiency));
    }
  }, [isOpen, initialGradeDeficiency]);

  const updateCell = useCallback((grade: number, value: string) => {
    setRows((prev) => ({ ...prev, [grade]: sanitizeCreditsFromSource(value) }));
  }, []);

  const handleSave = useCallback(() => {
    onSave?.(rowsToGradeDeficiency(rows));
    onClose();
  }, [rows, onSave, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <NeonModalDrawer
      id={CONFIGURATIONS_ID.CreditDeficiencyPanel}
      neonDrawerHasClosed={onClose}
      dataSize="medium"
      dataShowCloseButton="true"
      dataShowHeaderDivider="true"
      dataShowFooterDivider="true"
      dataCloseOnBackdropClick="true"
    >
      <div data-slot="drawer-header-title">{CREDIT_DEFICIENCY_PANEL_TITLE}</div>

      <div data-slot="drawer-body">
        <p style={{ ...bodyTextStyle, marginBottom: '16px' }}>{CREDIT_DEFICIENCY_DESCRIPTION}</p>
        <p style={{ ...bodyTextStyle, marginBottom: '20px' }}>
          {CREDIT_DEFICIENCY_NOTE} <strong>{CREDIT_DEFICIENCY_NOTE_BOLD}</strong>
          {CREDIT_DEFICIENCY_NOTE_SUFFIX}
        </p>

        <div style={panelTableWrapperStyle}>
          <table style={panelTableStyle}>
            <thead>
              <tr>
                <th style={{ ...panelTableHeaderStyle, ...panelTableGradeColStyle }}>
                  {CREDIT_DEFICIENCY_PANEL_COL_GRADE}
                </th>
                <th style={{ ...panelTableHeaderStyle, ...panelTableDeficiencyMinColStyle }}>
                  {CREDIT_DEFICIENCY_PANEL_COL_MIN_CUMULATIVE}
                </th>
              </tr>
            </thead>
            <tbody>
              {CREDIT_DEFICIENCY_GRADES.map((grade) => (
                <tr key={grade}>
                  <td style={{ ...panelTableCellStyle, ...panelTableGradeColStyle }}>{grade}</td>
                  <td style={{ ...panelTableCellStyle, ...panelTableDeficiencyMinColStyle }}>
                    <input
                      type="number"
                      step="any"
                      inputMode="decimal"
                      min={0}
                      value={rows[grade] ?? ''}
                      onChange={(e) => updateCell(grade, e.target.value)}
                      style={panelInputStyle}
                      placeholder={CREDIT_DEFICIENCY_NO_MINIMUM}
                      aria-label={`Grade ${grade} minimum cumulative credits earned`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div data-slot="drawer-button-footer">
        <NeonButtonFooter
          id={`${CONFIGURATIONS_ID.CreditDeficiencyPanel}-footer`}
          buttons={[
            {
              id: CONFIGURATIONS_ID.CreditDeficiencyPanelCancel,
              text: CREDIT_DEFICIENCY_PANEL_CANCEL,
              type: NeonButtonTypes.Borderless,
              onClick: onClose,
            },
          ]}
          primaryButton={{
            id: CONFIGURATIONS_ID.CreditDeficiencyPanelSave,
            text: CREDIT_DEFICIENCY_PANEL_SAVE,
            type: NeonButtonTypes.Primary,
            onClick: handleSave,
          }}
        />
      </div>
    </NeonModalDrawer>
  );
}
